import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Divider,
  Typography,
  Tooltip,
  ButtonGroup,
  Button
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterList as FilterListIcon,
  Sort as SortIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  AudioFile as AudioIcon,
  Description as DescriptionIcon,
  PictureAsPdf as PdfIcon,
  TableChart as SpreadsheetIcon,
  Slideshow as PresentationIcon,
  Code as CodeIcon,
  Archive as ArchiveIcon
} from '@mui/icons-material';

// File type filters with icons and descriptions
const fileTypeFilters = [
  { value: 'all', label: 'All Items', icon: <FileIcon fontSize="small" />, description: 'Show all files and folders' },
  { value: 'folders', label: 'Folders Only', icon: <FolderIcon fontSize="small" />, description: 'Show only folders' },
  { value: 'files', label: 'Files Only', icon: <FileIcon fontSize="small" />, description: 'Show only files' },
  { value: 'image', label: 'Images', icon: <ImageIcon fontSize="small" />, description: 'Photos, graphics, and images' },
  { value: 'video', label: 'Videos', icon: <VideoIcon fontSize="small" />, description: 'Video files and recordings' },
  { value: 'audio', label: 'Audio', icon: <AudioIcon fontSize="small" />, description: 'Music and audio files' },
  { value: 'google-apps.document', label: 'Google Docs', icon: <DescriptionIcon fontSize="small" />, description: 'Google Documents' },
  { value: 'google-apps.spreadsheet', label: 'Google Sheets', icon: <SpreadsheetIcon fontSize="small" />, description: 'Google Spreadsheets' },
  { value: 'google-apps.presentation', label: 'Google Slides', icon: <PresentationIcon fontSize="small" />, description: 'Google Presentations' },
  { value: 'pdf', label: 'PDF Files', icon: <PdfIcon fontSize="small" />, description: 'Portable Document Format files' },
  { value: 'text', label: 'Text Files', icon: <CodeIcon fontSize="small" />, description: 'Text documents and code files' },
  { value: 'archive', label: 'Archives', icon: <ArchiveIcon fontSize="small" />, description: 'ZIP, RAR, and other compressed files' }
];

// Sort options
const sortOptions = [
  { value: 'name', label: 'Name' },
  { value: 'modified', label: 'Modified Date' },
  { value: 'size', label: 'File Size' },
  { value: 'type', label: 'File Type' }
];

// eslint-disable-next-line max-lines-per-function
export default function DirectorySearch({
  searchQuery,
  onSearch,
  filterType,
  onFilterChange,
  sortBy,
  sortOrder,
  onSortChange
}) {
  const [searchInputValue, setSearchInputValue] = useState(searchQuery);
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const [sortMenuAnchor, setSortMenuAnchor] = useState(null);

  // Debounced search to avoid excessive API calls
  const debouncedSearch = useCallback((value) => {
    const timeoutId = setTimeout(() => {
      onSearch(value);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [onSearch]);

  const handleSearchChange = useCallback((event) => {
    const value = event.target.value;
    setSearchInputValue(value);

    // Clear previous timeout and set new one
    const cleanup = debouncedSearch(value);
    return cleanup;
  }, [debouncedSearch]);

  const handleSearchClear = useCallback(() => {
    setSearchInputValue('');
    onSearch('');
  }, [onSearch]);

  const handleFilterMenuOpen = useCallback((event) => {
    setFilterMenuAnchor(event.currentTarget);
  }, []);

  const handleFilterMenuClose = useCallback(() => {
    setFilterMenuAnchor(null);
  }, []);

  const handleFilterSelect = useCallback((newFilterType) => {
    onFilterChange(newFilterType);
    handleFilterMenuClose();
  }, [onFilterChange, handleFilterMenuClose]);

  const handleSortMenuOpen = useCallback((event) => {
    setSortMenuAnchor(event.currentTarget);
  }, []);

  const handleSortMenuClose = useCallback(() => {
    setSortMenuAnchor(null);
  }, []);

  const handleSortSelect = useCallback((newSortBy) => {
    onSortChange(newSortBy);
    handleSortMenuClose();
  }, [onSortChange, handleSortMenuClose]);

  // Get current filter info
  const currentFilter = useMemo(() => {
    return fileTypeFilters.find(filter => filter.value === filterType) || fileTypeFilters[0];
  }, [filterType]);

  // Get current sort info
  const currentSort = useMemo(() => {
    return sortOptions.find(sort => sort.value === sortBy) || sortOptions[0];
  }, [sortBy]);

  // Get active filters count for badge
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (searchQuery) count++;
    if (filterType !== 'all') count++;
    return count;
  }, [searchQuery, filterType]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Main search and filter bar */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        {/* Search input */}
        <TextField
          fullWidth
          size="small"
          placeholder="Search files and folders..."
          value={searchInputValue}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchInputValue && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleSearchClear}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            )
          }}
          sx={{ flex: 1 }}
        />

        {/* Filter button */}
        <Tooltip title="Filter by file type">
          <Button
            variant={filterType !== 'all' ? 'contained' : 'outlined'}
            startIcon={<FilterListIcon />}
            endIcon={currentFilter.icon}
            onClick={handleFilterMenuOpen}
            sx={{ minWidth: 120 }}
          >
            {currentFilter.label}
            {activeFiltersCount > 0 && (
              <Chip
                label={activeFiltersCount}
                size="small"
                color="primary"
                sx={{ ml: 1, height: 20, fontSize: '0.75rem' }}
              />
            )}
          </Button>
        </Tooltip>

        {/* Sort button group */}
        <ButtonGroup variant="outlined" size="small">
          <Button
            startIcon={<SortIcon />}
            onClick={handleSortMenuOpen}
            sx={{ minWidth: 100 }}
          >
            {currentSort.label}
          </Button>
          <Button
            onClick={() => onSortChange(sortBy)}
            sx={{ minWidth: 40 }}
          >
            {sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
          </Button>
        </ButtonGroup>
      </Box>

      {/* Active filters display */}
      {(searchQuery || filterType !== 'all') && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Active filters:
          </Typography>

          {searchQuery && (
            <Chip
              label={`Search: "${searchQuery}"`}
              size="small"
              onDelete={handleSearchClear}
              color="primary"
              variant="outlined"
            />
          )}

          {filterType !== 'all' && (
            <Chip
              label={`Type: ${currentFilter.label}`}
              size="small"
              icon={currentFilter.icon}
              onDelete={() => onFilterChange('all')}
              color="primary"
              variant="outlined"
            />
          )}
        </Box>
      )}

      {/* Filter menu */}
      <Menu
        anchorEl={filterMenuAnchor}
        open={Boolean(filterMenuAnchor)}
        onClose={handleFilterMenuClose}
        PaperProps={{
          sx: { maxWidth: 300, maxHeight: 400 }
        }}
      >
        <MenuItem disabled>
          <Typography variant="subtitle2">Filter by type</Typography>
        </MenuItem>
        <Divider />

        {fileTypeFilters.map((filter) => (
          <MenuItem
            key={filter.value}
            onClick={() => handleFilterSelect(filter.value)}
            selected={filter.value === filterType}
            sx={{ py: 1 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
              {filter.icon}
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2">{filter.label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {filter.description}
                </Typography>
              </Box>
            </Box>
          </MenuItem>
        ))}
      </Menu>

      {/* Sort menu */}
      <Menu
        anchorEl={sortMenuAnchor}
        open={Boolean(sortMenuAnchor)}
        onClose={handleSortMenuClose}
      >
        <MenuItem disabled>
          <Typography variant="subtitle2">Sort by</Typography>
        </MenuItem>
        <Divider />

        {sortOptions.map((sort) => (
          <MenuItem
            key={sort.value}
            onClick={() => handleSortSelect(sort.value)}
            selected={sort.value === sortBy}
          >
            <Typography variant="body2">{sort.label}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
